import type { ComponentProps, ReactNode } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@/lib/utils';

/** Bottom sheet — the mobile shape of every modal, with the 20px top radius. */
export const Drawer = DrawerPrimitive.Root;
export const DrawerTrigger = DrawerPrimitive.Trigger;
export const DrawerClose = DrawerPrimitive.Close;

export interface DrawerContentProps extends ComponentProps<typeof DrawerPrimitive.Content> {
  title: string;
  /** Right-hand side of the title row, e.g. the "30-Min-Raster" hint. */
  titleAside?: ReactNode;
  srDescription?: string;
  children: ReactNode;
}

export function DrawerContent({
  className,
  title,
  titleAside,
  srDescription,
  children,
  ...props
}: DrawerContentProps) {
  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
      <DrawerPrimitive.Content
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-sheet bg-card',
          'px-4 pt-2.5 outline-none',
          className,
        )}
        style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
        {...props}
      >
        <div className="flex justify-center pb-2">
          <div aria-hidden className="h-1 w-9 rounded-sm bg-line" />
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <DrawerPrimitive.Title className="text-card font-bold text-fg">{title}</DrawerPrimitive.Title>
          {titleAside}
        </div>
        {srDescription ? (
          <DrawerPrimitive.Description className="sr-only">{srDescription}</DrawerPrimitive.Description>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </DrawerPrimitive.Content>
    </DrawerPrimitive.Portal>
  );
}
